import styled from '@emotion/styled';
import { Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { MessageTestingProgressBar } from './ProgressBar';
import { MessageTestingResultsTabs } from './ResultsTabs';
import { useExportMessageTestingPpt } from '@trinity/chat-components/hooks/useExportMessageTestingPpt';
import { useMessageTestingRun } from '@trinity/chat-components/hooks/useMessageTestingRun';
import type { SegmentOption, TestableMessage } from './types';

interface MessageTestingResultsPanelProps {
  runIdentifier: string;
  onClose: () => void;
}

export function MessageTestingResultsPanel({
  runIdentifier,
  onClose,
}: MessageTestingResultsPanelProps): React.JSX.Element {
  const apiClient = useChatApiClient();

  const { data: runInputs, isLoading: isLoadingInputs } = useQuery({
    queryKey: ['message-testing-run-inputs', runIdentifier],
    queryFn: () => apiClient.messageTesting.getRun(runIdentifier),
  });

  const messages: TestableMessage[] = runInputs?.messages ?? [];
  const segments: SegmentOption[] = runInputs?.segments ?? [];
  const brand = runInputs?.brand;
  const objective = runInputs?.objective;

  const { status, progress, cells, cellErrors, error, rollups } = useMessageTestingRun(runIdentifier);
  const { exporting, exportPpt } = useExportMessageTestingPpt(runIdentifier);

  if (isLoadingInputs) {
    return (
      <Root>
        <PanelHeader>
          <PanelTitle>Simulated Message Testing Results</PanelTitle>
          <HeaderActions>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </HeaderActions>
        </PanelHeader>
        <LoadingContainer>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="rectangular" width="100%" height={200} />
        </LoadingContainer>
      </Root>
    );
  }

  return (
    <Root>
      <PanelHeader>
        <PanelTitle>Simulated Message Testing Results</PanelTitle>
        <HeaderActions>
          {(status === 'analyzing' || status === 'completed') && (
            <ExportButton onClick={exportPpt} disabled={exporting}>
              {exporting ? 'Exporting…' : 'Download PPT'}
            </ExportButton>
          )}
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </HeaderActions>
      </PanelHeader>

      <MessageTestingProgressBar status={status} progress={progress} error={error} />

      {(brand || objective) && (
        <RunMeta>
          {brand && (
            <RunMetaItem>
              <strong>Brand:</strong> {brand}
            </RunMetaItem>
          )}
          {objective && (
            <RunMetaItem>
              <strong>Objective:</strong> {objective}
            </RunMetaItem>
          )}
        </RunMeta>
      )}

      {(cells.length > 0 || messages.length > 0) && (
        <MessageTestingResultsTabs
          messages={messages}
          segments={segments}
          cells={cells}
          cellErrors={cellErrors}
          rollups={rollups}
        />
      )}
    </Root>
  );
}

// ─── Panel-specific styled components ───────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  height: 100%;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const PanelTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: white;
  background: #6366f1;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #4f46e5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 0 4px;

  &:hover {
    color: #1a1a1a;
  }
`;

const RunMeta = styled.div`
  padding: 10px 16px;
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #333;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e0e0;
`;

const RunMetaItem = styled.span`
  strong {
    color: #555;
    margin-right: 4px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 16px;
`;
